import { supabase } from "./supabase";

// UPC → identity correction memory (migration 0067).
//
// Two-tier architecture:
//   * barcode_identity_corrections — GLOBAL. Admin-written.
//     First-class crowd-verified mappings (UPC → canonical).
//   * user_scan_corrections.barcode_upc — family-scoped.
//     Regular users' corrections land here; admins later promote.
//
// Read path tries global first; falls back to the family-scoped
// row if the user has taught something admins haven't curated yet.
// Result: admin-curated scans apply to everyone; a family still
// sees their own teaching even before promotion.

// "misc" is the kitchen's catchall sentinel — a tile id every
// location registry exports as a final fallback. Treating it as a
// real placement signal during reads teaches every future scan the
// "I gave up" answer instead of the canonical's hub-chain default
// (e.g. gochugaru → spices_dried_herbs). Strip it on read so a
// downstream tier (kitchen-home → tag-hints → canonical-info) can
// resolve a real tile.
function dropMiscTile(tileId) {
  return tileId === "misc" ? null : tileId;
}

function fromGlobal(row) {
  if (!row) return null;
  return {
    source:          "global",
    barcodeUpc:      row.barcode_upc,
    canonicalId:     row.canonical_id || null,
    typeId:          row.type_id || null,
    tileId:          dropMiscTile(row.tile_id) || null,
    location:        row.location || null,
    emoji:           row.emoji || null,
    ingredients:   Array.isArray(row.ingredient_ids) ? row.ingredient_ids : [],
    correctionCount: row.correction_count || 1,
    // Fields populated by the external-baseline ingest (migration
    // 0130 / scripts/ingest_usda_branded.js). All nullable — legacy
    // admin-written rows don't carry them. source_provenance says
    // which external source set which field ("admin" = locked).
    brand:              row.brand || null,
    // variant — flavor / product line (migration 0160). null on
    // pre-migration rows; new scans populate it via the segmentation
    // path in ScanPipeline so re-scans get the SPECIFIC product back.
    variant:            row.variant || null,
    name:               row.name || null,
    packageSizeAmount:  row.package_size_amount ?? null,
    packageSizeUnit:    row.package_size_unit || null,
    imageUrl:           row.image_url || null,
    categoryHints:      Array.isArray(row.category_hints) ? row.category_hints : [],
    sourceProvenance:   row.source_provenance || {},
    lastExternalSync:   row.last_external_sync || null,
    // Migration 0144 — full-product capture columns. state / cut
    // mirror pantry_items axes; claims is the front-photo
    // marketing/dietary token list; productMetadata is a JSONB
    // grab-bag for storage_instructions / container / servings /
    // certifications / variant / country / allergens / cooking_method
    // / brand_color / front_of_pack_marks / date_format_hint /
    // manufacturer (callers reach into it by key).
    state:              row.state || null,
    cut:                row.cut || null,
    claims:             Array.isArray(row.claims) ? row.claims : [],
    productMetadata:    row.product_metadata || {},
  };
}

function fromFamily(row) {
  if (!row) return null;
  return {
    source:          "family",
    barcodeUpc:      row.barcode_upc,
    canonicalId:     row.canonical_id || null,
    typeId:          row.type_id || null,
    tileId:          dropMiscTile(row.tile_id) || null,
    location:        row.location || null,
    emoji:           row.emoji || null,
    ingredients:   Array.isArray(row.ingredient_ids) ? row.ingredient_ids : [],
    correctionCount: row.correction_count || 1,
    // brand has lived on user_scan_corrections since migration 0062;
    // name / package_size_* arrived in migration 0140 to bring the
    // family tier to parity with barcode_identity_corrections (0130).
    // The remaining external-baseline fields (imageUrl, categoryHints,
    // sourceProvenance, lastExternalSync) only ever populate on global
    // rows — the USDA ingest writes admin-tier — so they stay null
    // here for call-site shape parity across tiers.
    brand:              row.brand || null,
    variant:            row.variant || null,  // migration 0160 parity
    name:               row.name || null,
    packageSizeAmount:  row.package_size_amount ?? null,
    packageSizeUnit:    row.package_size_unit || null,
    imageUrl:           null,
    categoryHints:      [],
    sourceProvenance:   {},
    lastExternalSync:   null,
    // Migration 0144 parity — see fromGlobal.
    state:              row.state || null,
    cut:                row.cut || null,
    claims:             Array.isArray(row.claims) ? row.claims : [],
    productMetadata:    row.product_metadata || {},
  };
}

/**
 * Resolve a UPC to a prior correction, tiered global-first.
 *
 * Returns { source, canonicalId, typeId, emoji, ingredients,
 * correctionCount } or null if no correction has been taught for
 * this UPC yet.
 */
export async function findBarcodeCorrection(barcodeUpc) {
  if (!barcodeUpc) return null;
  const upc = String(barcodeUpc).trim();
  if (!upc) return null;

  // Build a list of equivalent UPC forms to query against. Different
  // scanners / migrations / vendors stored the same physical code
  // in a few different digit-counts (11-short, 12-UPC-A, 13-EAN-13).
  // Querying with .in() against all variants finds the row no matter
  // which form it was originally written under, so users don't have
  // to re-teach corrections after the canonicalize-at-capture
  // change.
  const variants = (() => {
    const d = upc.replace(/\D+/g, "");
    if (d.length < 8 || d.length > 14) return [upc];
    if (d.length === 8) return [d];                 // EAN-8 separate family
    const stripped = d.replace(/^0+/, "") || d;
    const padded12 = stripped.padStart(12, "0");
    const padded13 = stripped.padStart(13, "0");
    // De-dup with a Set in case the input was already canonical.
    return Array.from(new Set([upc, d, stripped, padded12, padded13]));
  })();

  // Tier 1 — global. One row per UPC (unique constraint). Any
  // auth'd user reads (public-read RLS).
  {
    const { data, error } = await supabase
      .from("barcode_identity_corrections")
      .select("*")
      .in("barcode_upc", variants)
      .limit(1);
    if (error) {
      console.warn("[barcode_identity_corrections] select failed:", error.message);
    } else if (data && data.length > 0) {
      return fromGlobal(data[0]);
    }
  }

  // Tier 2 — family-scoped. RLS from 0046 limits to self+family.
  // May return multiple rows (one per family member who corrected
  // the same UPC); pick the most recently used across any variant.
  {
    const { data, error } = await supabase
      .from("user_scan_corrections")
      .select("*")
      .in("barcode_upc", variants)
      .order("last_used_at", { ascending: false })
      .limit(1);
    if (error) {
      console.warn("[user_scan_corrections:upc] select failed:", error.message);
      return null;
    }
    if (data && data.length > 0) return fromFamily(data[0]);
  }

  return null;
}

/**
 * Record a UPC correction. Admins write to the global table so all
 * users benefit; regular users write to the family-scoped table.
 *
 * Best-effort: errors log and return { error } but never throw.
 */
// OFF category hints that are too generic to map to a canonical at
// Tier-1 exact confidence. "beverages" matches every soda / juice /
// water equally, so teaching "beverages → soft_drink" would mistag
// every scanned juice box as a soda. These get filtered OUT of the
// tag-map seeding — the narrower siblings (colas, sodas, sparkling-
// waters) carry the actual discriminating signal.
const GENERIC_OFF_TAGS = new Set([
  "beverages", "drinks", "foods", "snacks", "meals", "dishes",
  "plant-based-foods", "plant-based-beverages",
  "ultra-processed-foods", "processed-foods", "fresh-foods",
  "frozen-foods", "canned-foods",
  "dairies", "dairy-products",
  "meats", "fishes", "seafoods",
  "fruits", "vegetables",
  "sauces", "condiments", "seasonings",
  "sweet-snacks", "salty-snacks", "savory-snacks",
  "sugary-drinks", "sugary-snacks", "sugary-foods",
  "fatty-foods",
  "breakfasts",
]);

export async function rememberBarcodeCorrection({
  userId,
  isAdmin,
  barcodeUpc,
  canonicalId,
  typeId,
  tileId,             // migration 0129 — STORED IN shelf override
  location,           // migration 0129 — fridge/pantry/freezer override
  emoji,
  ingredients,
  brand,              // migration 0062 (family) / 0130 (global)
  // Migration 0160 — variant axis (flavor / product line under brand).
  // Lets a single (canonical, brand) UPC like Pepsi Wild Cherry vs
  // Diet Pepsi vs regular Pepsi keep distinct correction rows so a
  // re-scan pre-fills the SPECIFIC product, not just the brand.
  variant,
  name,               // migration 0140 (family) / 0130 (global) — product display name
  packageSizeAmount,  // migration 0140 (family) / 0130 (global) — numeric
  packageSizeUnit,    // migration 0140 (family) / 0130 (global) — "oz" / "g" / etc.
  categoryHints = null,   // optional — admin path seeds the tag map when present
  // Migration 0144 — full-product capture. All optional. state / cut
  // mirror the pantry_items axes; claims is a string array of
  // marketing/dietary tokens; productMetadata is the JSONB grab-bag
  // for everything else extractable from package photos.
  state,
  cut,
  claims,
  productMetadata,
}) {
  if (!userId || !barcodeUpc) return { error: new Error("userId + barcodeUpc required") };
  const upc = String(barcodeUpc).trim();
  if (!upc) return { error: new Error("empty upc") };

  // Build patch — only include supplied fields so a narrow update
  // (e.g. canonical-only) doesn't wipe prior type/emoji/tile.
  const patch = {};
  if (canonicalId) patch.canonical_id = canonicalId;
  if (typeId)      patch.type_id      = typeId;
  if (tileId)      patch.tile_id      = tileId;
  if (location)    patch.location     = location;
  if (emoji)       patch.emoji        = emoji;
  if (Array.isArray(ingredients) && ingredients.length > 0) {
    patch.ingredient_ids = ingredients;
  }
  // Product-detail fields. Skip empty strings so a caller passing
  // "" doesn't blow away a real prior value with whitespace.
  const trimmedBrand   = typeof brand === "string" ? brand.trim() : null;
  const trimmedVariant = typeof variant === "string" ? variant.trim() : null;
  const trimmedName    = typeof name === "string" ? name.trim() : null;
  if (trimmedBrand)   patch.brand   = trimmedBrand;
  if (trimmedVariant) patch.variant = trimmedVariant;
  if (trimmedName)    patch.name    = trimmedName;
  if (Number.isFinite(packageSizeAmount) && packageSizeAmount > 0) {
    patch.package_size_amount = packageSizeAmount;
  }
  if (typeof packageSizeUnit === "string" && packageSizeUnit.trim()) {
    patch.package_size_unit = packageSizeUnit.trim();
  }
  // Migration 0144 — full-product capture. Each field skipped when
  // null/empty so a partial caller doesn't wipe priors. claims and
  // productMetadata only land if they have actual content (non-empty
  // array / non-empty object).
  const trimmedState = typeof state === "string" ? state.trim() : null;
  const trimmedCut   = typeof cut === "string" ? cut.trim() : null;
  if (trimmedState) patch.state = trimmedState;
  if (trimmedCut)   patch.cut   = trimmedCut;
  if (Array.isArray(claims) && claims.length > 0) {
    // Filter to non-empty strings + dedupe so we don't write
    // ["Organic", "Organic", "", null] verbatim. Order preserved.
    const seen = new Set();
    const cleaned = [];
    for (const c of claims) {
      const t = typeof c === "string" ? c.trim() : "";
      if (!t) continue;
      const key = t.toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      cleaned.push(t);
    }
    if (cleaned.length > 0) patch.claims = cleaned;
  }
  if (productMetadata && typeof productMetadata === "object" && !Array.isArray(productMetadata)) {
    // Strip null / undefined / empty-string entries so a caller
    // can pass a partial object (e.g. only `storage_instructions`)
    // without overwriting existing JSONB keys with empty values.
    // The DB-side merge is whole-column replace by default — for a
    // proper merge we'd jsonb_set per key, but for v1 just write
    // what the caller provided when at least one key is meaningful.
    const cleaned = {};
    for (const [k, v] of Object.entries(productMetadata)) {
      if (v == null) continue;
      if (typeof v === "string" && !v.trim()) continue;
      if (Array.isArray(v) && v.length === 0) continue;
      cleaned[k] = v;
    }
    if (Object.keys(cleaned).length > 0) patch.product_metadata = cleaned;
  }
  if (Object.keys(patch).length === 0) {
    // Loud no-op so a caller that silently passes nothing-to-teach
    // (e.g. a chip pick that resolves to the same value already
    // stored) shows up in the console rather than vanishing into
    // "the write succeeded with nothing." Surface it so callers can
    // diagnose teach-on-edit gaps instead of guessing.
    console.warn("[barcodeCorrections] no fields to write — caller passed an empty patch", { barcodeUpc: upc });
    return { error: null, skipped: true };
  }

  if (isAdmin) {
    // Global path — upsert by barcode_upc (unique index).
    const { data: existing, error: selErr } = await supabase
      .from("barcode_identity_corrections")
      .select("id, correction_count, source_provenance")
      .eq("barcode_upc", upc)
      .maybeSingle();
    if (selErr) {
      console.warn("[barcode_identity_corrections] select failed:", selErr.message);
      return { error: selErr };
    }
    // Stamp provenance for every field this write touches. Locks the
    // field against later external-baseline re-ingests (migration
    // 0130 / scripts/ingest_usda_branded.js) — the ingest merge logic
    // refuses to overwrite any field whose provenance is "admin".
    // Patch-key → column-name mapping is identity for the columns we
    // stamp; ingredients isn't locked because the tag-map path has
    // its own admin-write flow.
    const provKeys = Object.keys(patch).filter(k =>
      ["canonical_id", "type_id", "tile_id", "location", "emoji",
       "brand", "name", "package_size_amount", "package_size_unit",
       "state", "cut", "claims", "product_metadata"].includes(k)
    );
    const nextProv = { ...(existing?.source_provenance || {}) };
    for (const k of provKeys) nextProv[k] = "admin";
    if (existing) {
      const { error } = await supabase
        .from("barcode_identity_corrections")
        .update({
          ...patch,
          source_provenance: nextProv,
          correction_count: (existing.correction_count || 1) + 1,
          last_used_at: new Date().toISOString(),
        })
        .eq("id", existing.id);
      if (error) {
        console.warn("[barcode_identity_corrections] update failed:", error.message);
        return { error };
      }
      await seedTagMap({ userId, canonicalId, categoryHints });
      return { error: null, source: "global", existed: true };
    }
    // No existing row from the select. Use upsert (not raw insert) so a
    // concurrent admin write for the same UPC — which would have read
    // the same "no existing" result and tried to insert simultaneously —
    // updates the conflicting row instead of throwing on the
    // barcode_identity_corrections_barcode_upc_key unique constraint.
    // Without this, two scans of the same UPC by the admin in the same
    // window 23505'd one of them and tore down the whole submit flow.
    const { error } = await supabase
      .from("barcode_identity_corrections")
      .upsert({
        barcode_upc: upc,
        ...patch,
        source_provenance: nextProv,
        correction_count: 1,
        last_used_at: new Date().toISOString(),
        created_by: userId,
      }, { onConflict: "barcode_upc" });
    if (error) {
      console.warn("[barcode_identity_corrections] upsert failed:", error.message);
      return { error };
    }
    await seedTagMap({ userId, canonicalId, categoryHints });
    return { error: null, source: "global", existed: false };
  }

  // Family path — upsert into user_scan_corrections keyed on
  // (user_id, barcode_upc). raw_text mirrors the UPC so the text-
  // keyed index from 0046 stays populated (defensive — a user who
  // later types the barcode digits still hits memory).
  const { data: existingRows, error: selErr } = await supabase
    .from("user_scan_corrections")
    .select("*")
    .eq("user_id", userId)
    .eq("barcode_upc", upc)
    .limit(1);
  if (selErr) {
    console.warn("[user_scan_corrections:upc] select failed:", selErr.message);
    return { error: selErr };
  }
  const existing = existingRows && existingRows[0];
  if (existing) {
    const { error } = await supabase
      .from("user_scan_corrections")
      .update({
        ...patch,
        correction_count: (existing.correction_count || 1) + 1,
        last_used_at: new Date().toISOString(),
      })
      .eq("id", existing.id);
    if (error) {
      console.warn("[user_scan_corrections:upc] update failed:", error.message);
      return { error };
    }
    return { error: null, source: "family", existed: true };
  }
  const { error } = await supabase
    .from("user_scan_corrections")
    .insert({
      user_id: userId,
      raw_text: upc,
      raw_text_normalized: upc,
      barcode_upc: upc,
      ...patch,
      correction_count: 1,
      last_used_at: new Date().toISOString(),
    });
  if (error) {
    console.warn("[user_scan_corrections:upc] insert failed:", error.message);
    return { error };
  }
  return { error: null, source: "family", existed: false };
}

/**
 * UPC-keyed package-size consensus, computed on the fly across every
 * household's pantry_items rows. Returns { amount, unit, n } of the
 * most-common (max, unit) pair for this UPC — or null when no rows
 * have been committed for it yet.
 *
 * Voting model: every committed pantry_items row IS a vote. A single
 * fat-fingered entry doesn't flip the consensus; it takes a majority
 * of observations to swing the answer. Reads via the
 * `popular_package_size_for_upc` RPC (migration 0142) which uses
 * SECURITY DEFINER to safely aggregate across families.
 *
 * Best-effort: errors log + return null. Never throws — a stale or
 * missing consensus is fine, the user just types the size manually.
 */
export async function fetchPopularPackageSizeForUpc(barcodeUpc) {
  if (!barcodeUpc) return null;
  const upc = String(barcodeUpc).trim();
  if (!upc) return null;
  const { data, error } = await supabase
    .rpc("popular_package_size_for_upc", { p_upc: upc, p_limit: 1 });
  if (error) {
    // 42883 = function does not exist (migration 0142 not applied).
    // Silent noop so old envs keep working.
    if (error.code === "42883") return null;
    console.warn("[popular_package_size_for_upc] rpc failed:", error.message);
    return null;
  }
  if (!Array.isArray(data) || data.length === 0) return null;
  const top = data[0];
  if (!top || top.amount == null || !top.unit) return null;
  return {
    amount: Number(top.amount),
    unit:   String(top.unit),
    n:      Number(top.n) || 1,
  };
}

/**
 * UPC-keyed price consensus, computed across every household's
 * recent kitchen-item commits for this UPC. Returns the median
 * price (cents), sample size, and freshness in days — or null when
 * no observations exist within the rolling 90-day window.
 *
 * Voting model: every committed kitchen row carrying a price IS a
 * vote. A single fat-fingered entry can't flip the consensus;
 * majority observations win. Reads via the
 * `popular_price_for_upc` RPC (migration 0143) which uses
 * SECURITY DEFINER to safely aggregate across families.
 *
 * NOT wired into UI yet — call sites can read it now if they want
 * to surface a per-row price chip, but the canonical surfacing
 * decision (per-row chip + trip total) is deferred until the data
 * is dense enough to be useful. Best-effort: errors log and return
 * null; never throws.
 */
export async function fetchPopularPriceForUpc(barcodeUpc) {
  if (!barcodeUpc) return null;
  const upc = String(barcodeUpc).trim();
  if (!upc) return null;
  const { data, error } = await supabase
    .rpc("popular_price_for_upc", { p_upc: upc });
  if (error) {
    if (error.code === "42883") return null;        // RPC not deployed yet
    console.warn("[popular_price_for_upc] rpc failed:", error.message);
    return null;
  }
  if (!Array.isArray(data) || data.length === 0) return null;
  const top = data[0];
  if (!top || top.price_cents == null) return null;
  return {
    priceCents:    Number(top.price_cents),
    observations:  Number(top.observations) || 1,
    freshnessDays: Number(top.freshness_days) || 0,
  };
}

/**
 * (canonical_id, brand)-keyed price consensus. Use when an item has
 * no UPC (typed-from-scratch entries) but does resolve to a
 * canonical + brand pair. Pass brand=null for the brand-agnostic
 * canonical-wide median.
 *
 * Same shape and tier semantics as fetchPopularPriceForUpc.
 */
export async function fetchPopularPriceForCanonicalBrand(canonicalId, brand) {
  if (!canonicalId) return null;
  const canon = String(canonicalId).trim();
  if (!canon) return null;
  const trimmedBrand = typeof brand === "string" ? brand.trim() : null;
  const { data, error } = await supabase
    .rpc("popular_price_for_canonical_brand", {
      p_canonical: canon,
      p_brand:     trimmedBrand || null,
    });
  if (error) {
    if (error.code === "42883") return null;
    console.warn("[popular_price_for_canonical_brand] rpc failed:", error.message);
    return null;
  }
  if (!Array.isArray(data) || data.length === 0) return null;
  const top = data[0];
  if (!top || top.price_cents == null) return null;
  return {
    priceCents:    Number(top.price_cents),
    observations:  Number(top.observations) || 1,
    freshnessDays: Number(top.freshness_days) || 0,
  };
}

// Seed the Tier-1 learned tag map. For each OFF categoryHint that
// was on the corrected scan's row, upsert a (tag → canonical_id)
// mapping into off_category_tag_canonicals. Next fresh scan of a
// different UPC that carries any of the same hints hits Tier 1 at
// "exact" confidence and auto-lands on this canonical — no more
// per-product rewire for the same semantic class (sodas, yogurts,
// chicken soups, etc.).
//
// Guards:
//   - Admin-gated at the RLS layer; non-admin callers just eat the
//     error silently (we warn on the first row and move on).
//   - GENERIC_OFF_TAGS filtered out so "beverages" doesn't become
//     a catch-all pointing at whichever beverage was last
//     corrected.
//   - Silent noop when migration 0123 hasn't been applied yet —
//     PostgREST returns 42P01 (undefined_table) which we swallow so
//     old envs keep working.
async function seedTagMap({ userId, canonicalId, categoryHints }) {
  if (!canonicalId || !userId) return;
  if (!Array.isArray(categoryHints) || categoryHints.length === 0) return;
  const now = new Date().toISOString();
  const tags = Array.from(new Set(
    categoryHints
      .map(t => String(t || "").trim().toLowerCase())
      .filter(t => t && !GENERIC_OFF_TAGS.has(t))
  ));
  if (tags.length === 0) return;
  for (const tag of tags) {
    // Read-then-write so correction_count bumps rather than
    // resetting to 1 every time the mapping re-lands on the same
    // canonical. Cheap — tags is small (usually < 10 per scan).
    const { data: existing, error: selErr } = await supabase
      .from("off_category_tag_canonicals")
      .select("off_tag, canonical_id, correction_count")
      .eq("off_tag", tag)
      .maybeSingle();
    if (selErr) {
      if (selErr.code === "42P01") return;    // migration not applied — silent noop
      console.warn("[off_tag_map] select failed:", selErr.message);
      continue;
    }
    if (existing && existing.canonical_id === canonicalId) {
      await supabase
        .from("off_category_tag_canonicals")
        .update({
          correction_count: (existing.correction_count || 1) + 1,
          last_used_at: now,
        })
        .eq("off_tag", tag);
    } else if (existing) {
      // Mapping conflict — latest correction wins, count resets.
      // The admin just told us this tag means `canonicalId`; trust
      // the most recent signal. Future: could keep per-canonical
      // counts instead of one row per tag, but v1 keeps shape
      // simple.
      await supabase
        .from("off_category_tag_canonicals")
        .update({
          canonical_id: canonicalId,
          correction_count: 1,
          last_used_at: now,
        })
        .eq("off_tag", tag);
    } else {
      await supabase
        .from("off_category_tag_canonicals")
        .insert({
          off_tag: tag,
          canonical_id: canonicalId,
          correction_count: 1,
          last_used_at: now,
          created_by: userId,
        });
    }
  }
}
